import { useCallback, useEffect, useState } from "react";
import useAxios from "../utilities/useAxios";
import { syncAppBadge } from "../utilities/appBadge";

export const NOTIFICATIONS_UPDATED_EVENT = "notificationsUpdated";

export function dispatchNotificationsUpdated() {
	window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT));
}

export async function markNotificationAsRead(notificationId) {
	if (!notificationId) {
		return;
	}
	await useAxios.put(`users/notifications/${notificationId}/read/`);
	dispatchNotificationsUpdated();
}

export async function markAllNotificationsAsRead() {
	await useAxios.put("users/notifications/read-all/");
	dispatchNotificationsUpdated();
}

export function useNotifications({ enabled = true } = {}) {
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);

	const fetchUnreadCount = useCallback(async () => {
		if (!enabled) {
			setUnreadCount(0);
			syncAppBadge(0);
			return;
		}

		try {
			const response = await useAxios.get(
				"users/notifications/unread-count/",
			);
			const count = response.data.count || 0;
			setUnreadCount(count);
			syncAppBadge(count);
		} catch (error) {
			console.error(
				"Error fetching unread notification count:",
				error,
			);
		}
	}, [enabled]);

	const fetchNotifications = useCallback(async () => {
		if (!enabled) {
			setNotifications([]);
			return [];
		}

		setLoading(true);
		try {
			const response = await useAxios.get(
				"users/notifications/",
				{
					params: { limit: 30 },
				},
			);
			const items = response.data.notifications || [];
			setNotifications(items);
			return items;
		} catch (error) {
			console.error("Error fetching notifications:", error);
			setNotifications([]);
			return [];
		} finally {
			setLoading(false);
		}
	}, [enabled]);

	const markAsRead = useCallback(async (notificationId) => {
		try {
			await markNotificationAsRead(notificationId);
			setNotifications((prev) =>
				prev.map((item) =>
					item.id === notificationId
						? { ...item, is_read: true }
						: item,
				),
			);
			setUnreadCount((prev) => {
				const next = Math.max(0, prev - 1);
				syncAppBadge(next);
				return next;
			});
		} catch (error) {
			console.error(
				"Error marking notification as read:",
				error,
			);
		}
	}, []);

	const markAllAsRead = useCallback(async () => {
		try {
			await markAllNotificationsAsRead();
			setNotifications((prev) =>
				prev.map((item) => ({
					...item,
					is_read: true,
				})),
			);
			setUnreadCount(0);
			syncAppBadge(0);
		} catch (error) {
			console.error(
				"Error marking all notifications as read:",
				error,
			);
		}
	}, []);

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		fetchUnreadCount();

		const handleNotificationsUpdated = () => {
			fetchUnreadCount();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				fetchUnreadCount();
			}
		};

		window.addEventListener(
			NOTIFICATIONS_UPDATED_EVENT,
			handleNotificationsUpdated,
		);
		document.addEventListener(
			"visibilitychange",
			handleVisibilityChange,
		);

		return () => {
			window.removeEventListener(
				NOTIFICATIONS_UPDATED_EVENT,
				handleNotificationsUpdated,
			);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
		};
	}, [enabled, fetchUnreadCount]);

	return {
		notifications,
		unreadCount,
		loading,
		fetchNotifications,
		fetchUnreadCount,
		markAsRead,
		markAllAsRead,
	};
}
